import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { normalizeSearchStr } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPin, Loader2, Users, Tag, ChevronDown, AlertTriangle, Plus, Trash2, Pencil, Check, X } from "lucide-react";

interface Conflict {
  recordType: "lead" | "client" | "invoice";
  recordId: string;
  name: string;
  clientType: string;
  currentRegionId: string;
  currentRegionName: string;
}

interface PreviewResponse {
  conflicts: Conflict[];
  proposedRegionStates: Array<{ regionId: string; regionName: string; current: string[]; proposed: string[] }>;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inPanel?: boolean;
}

interface RegionRow {
  id: string;
  name: string;
  clientTypeIds?: string[];
}

interface UserRow {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  profile?: { role?: string; allowedRegionIds?: string[] };
}

interface ClientTypeRow {
  id: string;
  name: string;
  isActive: boolean;
}

function userLabel(u: UserRow): string {
  const name = [u.firstName, u.lastName].filter(Boolean).join(" ").trim();
  return name || u.email || u.id;
}

function RegionAssigneesPopover({ region, users }: { region: RegionRow; users: UserRow[] }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const eligible = users.filter(u => {
    const role = u.profile?.role;
    return role !== "admin" && role !== "billing";
  });
  const seers = eligible.filter(u => {
    const list = u.profile?.allowedRegionIds || [];
    return list.length === 0 || list.includes(region.id);
  });
  const restricted = eligible.filter(u => (u.profile?.allowedRegionIds || []).length > 0);

  const mutation = useMutation({
    mutationFn: async ({ userId, next }: { userId: string; next: string[] }) => {
      const res = await apiRequest("PATCH", `/api/admin/users/${userId}/region-access`, { allowedRegionIds: next });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      toast({ title: "Saved", description: "Region access updated." });
    },
    onError: (e: any) => {
      toast({ title: "Error", description: e?.message || "Failed.", variant: "destructive" });
    },
  });

  const toggleUser = (u: UserRow) => {
    const cur = u.profile?.allowedRegionIds || [];
    const has = cur.includes(region.id);
    const next = has ? cur.filter(x => x !== region.id) : [...cur, region.id];
    mutation.mutate({ userId: u.id, next });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-auto py-1.5"
          data-testid={`button-region-assignees-${region.id}`}
        >
          <Users className="w-3.5 h-3.5 mr-1.5" />
          {seers.length} {seers.length === 1 ? "user" : "users"}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        <div className="space-y-2">
          <div className="text-sm font-medium">Who sees "{region.name}"</div>
          <p className="text-xs text-muted-foreground">
            Users with no restrictions ("All regions") see every region automatically. Toggle below to add or remove a region from a restricted user's allow-list.
          </p>
          <div className="space-y-1 max-h-72 overflow-y-auto pt-1">
            {eligible.length === 0 && (
              <p className="text-xs text-muted-foreground px-2">No sales reps or artists found.</p>
            )}
            {eligible.map(u => {
              const list = u.profile?.allowedRegionIds || [];
              const unrestricted = list.length === 0;
              const checked = unrestricted || list.includes(region.id);
              return (
                <label
                  key={u.id}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded ${unrestricted ? "opacity-60 cursor-not-allowed" : "hover-elevate cursor-pointer"}`}
                  title={unrestricted ? "User is unrestricted (sees all regions)." : ""}
                  data-testid={`row-region-assignee-${region.id}-${u.id}`}
                >
                  <Checkbox
                    checked={checked}
                    disabled={unrestricted || mutation.isPending}
                    onCheckedChange={() => !unrestricted && toggleUser(u)}
                  />
                  <span className="text-sm flex-1">{userLabel(u)}</span>
                  {unrestricted && (
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0">all</Badge>
                  )}
                </label>
              );
            })}
          </div>
          {restricted.length === 0 && (
            <p className="text-[11px] text-muted-foreground pt-1 border-t">
              Tip: every user is currently unrestricted. Restrict a user from Users &amp; Permissions → Roles → Visible Regions.
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function RegionClientTypePopover({
  region,
  clientTypes,
}: {
  region: RegionRow;
  clientTypes: ClientTypeRow[];
}) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const current = region.clientTypeIds || [];

  const mutation = useMutation({
    mutationFn: async (next: string[]) => {
      const res = await apiRequest("PATCH", `/api/regions/${region.id}`, { clientTypeIds: next });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/regions"] });
      toast({ title: "Saved", description: "Region client types updated." });
    },
    onError: (e: any) => {
      toast({ title: "Error", description: e?.message || "Failed.", variant: "destructive" });
    },
  });

  const toggle = (id: string) => {
    const has = current.includes(id);
    const next = has ? current.filter(x => x !== id) : [...current, id];
    mutation.mutate(next);
  };

  const setAll = () => mutation.mutate([]);

  const summary = current.length === 0
    ? "All client types"
    : current.length === 1
      ? clientTypes.find(c => c.id === current[0])?.name || "1 type"
      : `${current.length} types`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-auto py-1.5 gap-1.5"
          data-testid={`button-region-client-types-${region.id}`}
        >
          <Tag className="w-3.5 h-3.5" />
          <span className="text-xs">{summary}</span>
          <ChevronDown className="w-3 h-3 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72" align="end">
        <div className="space-y-2">
          <div className="text-sm font-medium">Client types for "{region.name}"</div>
          <p className="text-xs text-muted-foreground">
            When checked, this region will only appear in pickers and filters for those client types. Leave empty for "all".
          </p>
          <div className="flex gap-2 pt-1">
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs"
              disabled={mutation.isPending || current.length === 0}
              onClick={setAll}
              data-testid={`button-region-clear-types-${region.id}`}
            >
              Clear (all)
            </Button>
          </div>
          <div className="space-y-1 max-h-72 overflow-y-auto pt-1">
            {clientTypes.length === 0 && (
              <p className="text-xs text-muted-foreground px-2">No client types configured.</p>
            )}
            {clientTypes.map(ct => {
              const checked = current.includes(ct.id);
              return (
                <label
                  key={ct.id}
                  className="flex items-center gap-2 px-2 py-1.5 rounded hover-elevate cursor-pointer"
                  data-testid={`row-region-client-type-${region.id}-${ct.id}`}
                >
                  <Checkbox
                    checked={checked}
                    disabled={mutation.isPending}
                    onCheckedChange={() => toggle(ct.id)}
                  />
                  <span className="text-sm flex-1">{ct.name}</span>
                  {!ct.isActive && (
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0">inactive</Badge>
                  )}
                </label>
              );
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ConflictResolutionDialog({
  open,
  onOpenChange,
  conflicts,
  regions,
  clientTypes,
  payload,
  onResolved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conflicts: Conflict[];
  regions: RegionRow[];
  clientTypes: ClientTypeRow[];
  payload: { regionIds: string[]; mode: "add" | "replace" | "remove"; clientTypeIds: string[] };
  onResolved: () => void;
}) {
  const { toast } = useToast();
  // Map: recordKey "type:id" → newRegionId
  const [picks, setPicks] = useState<Record<string, string>>({});
  const [creatingFor, setCreatingFor] = useState<string | null>(null);
  const [newRegionName, setNewRegionName] = useState("");

  // Reset all picker state whenever the dialog opens or a fresh batch of conflicts arrives.
  // Without this, stale `picks` from a previous resolution can leak into a new operation.
  useEffect(() => {
    if (open) {
      setPicks({});
      setCreatingFor(null);
      setNewRegionName("");
    }
  }, [open, conflicts, payload]);

  // Group conflicts by client type for clarity
  const byClientType = useMemo(() => {
    const map = new Map<string, Conflict[]>();
    for (const c of conflicts) {
      if (!map.has(c.clientType)) map.set(c.clientType, []);
      map.get(c.clientType)!.push(c);
    }
    return Array.from(map.entries());
  }, [conflicts]);

  // For each conflict, compute which existing regions are valid targets — i.e. regions
  // that (after this bulk apply) will allow this record's clientType. A region's
  // post-apply clientTypeIds is empty (= unrestricted) OR includes the matching ID.
  const ctIdByName = useMemo(() => new Map(clientTypes.map(c => [c.name, c.id])), [clientTypes]);
  const proposedAllowedById = useMemo(() => {
    const map = new Map<string, Set<string> | null>();
    const targetSet = new Set(payload.regionIds);
    for (const r of regions) {
      const cur = r.clientTypeIds || [];
      let next: string[];
      if (!targetSet.has(r.id)) {
        next = cur;
      } else if (payload.mode === "replace") {
        next = [...payload.clientTypeIds];
      } else if (payload.mode === "add") {
        next = Array.from(new Set([...cur, ...payload.clientTypeIds]));
      } else {
        next = cur.filter(x => !payload.clientTypeIds.includes(x));
      }
      map.set(r.id, next.length === 0 ? null : new Set(next));
    }
    return map;
  }, [regions, payload]);

  const validRegionsFor = (clientTypeName: string): RegionRow[] => {
    const ctId = ctIdByName.get(clientTypeName);
    return regions.filter(r => {
      const allowed = proposedAllowedById.get(r.id);
      if (allowed === null || allowed === undefined) return true; // unrestricted
      return ctId ? allowed.has(ctId) : false;
    });
  };

  const createRegionMutation = useMutation({
    mutationFn: async ({ name, clientTypeName }: { name: string; clientTypeName: string }) => {
      // Pre-tag the new region with this conflict's client type so it shows up in scoped pickers.
      const ctId = ctIdByName.get(clientTypeName);
      const res = await apiRequest("POST", "/api/regions", {
        name,
        clientTypeIds: ctId ? [ctId] : [],
      });
      return res.json();
    },
    onSuccess: (newRegion: RegionRow, vars) => {
      queryClient.invalidateQueries({ queryKey: ["/api/regions"] });
      // Auto-pick this new region for every conflict in the same client type group.
      setPicks(prev => {
        const next = { ...prev };
        for (const c of conflicts) {
          if (c.clientType === vars.clientTypeName) {
            next[`${c.recordType}:${c.recordId}`] = newRegion.id;
          }
        }
        return next;
      });
      setCreatingFor(null);
      setNewRegionName("");
      toast({ title: "Region created", description: `"${newRegion.name}" was created and pre-assigned.` });
    },
    onError: (e: any) => {
      toast({ title: "Error", description: e?.message || "Failed to create region.", variant: "destructive" });
    },
  });

  const applyMutation = useMutation({
    mutationFn: async () => {
      const reassignments = conflicts.map(c => ({
        recordType: c.recordType,
        recordId: c.recordId,
        newRegionId: picks[`${c.recordType}:${c.recordId}`],
      }));
      const res = await apiRequest("POST", "/api/regions/bulk-apply", {
        ...payload,
        reassignments,
      });
      return res.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/regions"] });
      queryClient.invalidateQueries({ queryKey: ["/api/leads"] });
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
      queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
      toast({
        title: "Bulk update complete",
        description: `Reassigned ${data.leadsReassigned} lead(s), ${data.clientsReassigned} client(s), ${data.invoicesReassigned} invoice(s). Updated ${data.regionsUpdated} region(s).`,
      });
      onResolved();
      onOpenChange(false);
    },
    onError: (e: any) => {
      toast({ title: "Error", description: e?.message || "Failed.", variant: "destructive" });
    },
  });

  const allResolved = conflicts.every(c => !!picks[`${c.recordType}:${c.recordId}`]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            {conflicts.length} record{conflicts.length !== 1 ? "s" : ""} need{conflicts.length === 1 ? "s" : ""} a new region
          </DialogTitle>
          <DialogDescription>
            The regions you're about to restrict still contain records with client types that won't be allowed.
            Pick a new region for each — or create a new region if none fits.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {byClientType.map(([clientType, list]) => {
            const valid = validRegionsFor(clientType);
            const isCreating = creatingFor === clientType;
            return (
              <div key={clientType} className="rounded-md border" data-testid={`group-conflict-${clientType}`}>
                <div className="flex items-center justify-between gap-2 px-3 py-2 bg-muted/30 border-b">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs">{clientType}</Badge>
                    <span className="text-xs text-muted-foreground">
                      {list.length} record{list.length !== 1 ? "s" : ""} • {valid.length} valid region{valid.length !== 1 ? "s" : ""}
                    </span>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    onClick={() => { setCreatingFor(clientType); setNewRegionName(""); }}
                    data-testid={`button-create-region-for-${clientType}`}
                  >
                    <Plus className="w-3 h-3 mr-1" /> Create new region
                  </Button>
                </div>

                {isCreating && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-amber-50/50 dark:bg-amber-950/20 border-b">
                    <Input
                      value={newRegionName}
                      onChange={e => setNewRegionName(e.target.value)}
                      placeholder="New region name (e.g. Theaters - Northeast)"
                      className="h-8 text-sm"
                      autoFocus
                      data-testid={`input-new-region-name-${clientType}`}
                    />
                    <Button
                      size="sm"
                      className="h-8"
                      disabled={!newRegionName.trim() || createRegionMutation.isPending}
                      onClick={() => createRegionMutation.mutate({ name: newRegionName.trim(), clientTypeName: clientType })}
                      data-testid={`button-confirm-create-region-${clientType}`}
                    >
                      {createRegionMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Create"}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8"
                      onClick={() => { setCreatingFor(null); setNewRegionName(""); }}
                    >
                      Cancel
                    </Button>
                  </div>
                )}

                <div className="divide-y">
                  {list.map(c => {
                    const key = `${c.recordType}:${c.recordId}`;
                    const picked = picks[key] || "";
                    return (
                      <div
                        key={key}
                        className="flex items-center gap-3 px-3 py-2"
                        data-testid={`row-conflict-${c.recordType}-${c.recordId}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 text-sm">
                            <Badge variant="outline" className="text-[10px] px-1 py-0 capitalize">{c.recordType}</Badge>
                            <span className="font-medium truncate">{c.name}</span>
                          </div>
                          <div className="text-xs text-muted-foreground truncate">
                            Currently in <span className="font-medium">{c.currentRegionName}</span>
                          </div>
                        </div>
                        <Select
                          value={picked}
                          onValueChange={(v) => setPicks(p => ({ ...p, [key]: v }))}
                        >
                          <SelectTrigger className="w-56 h-8 text-xs" data-testid={`select-new-region-${c.recordType}-${c.recordId}`}>
                            <SelectValue placeholder="Pick a new region…" />
                          </SelectTrigger>
                          <SelectContent>
                            {valid.length === 0 ? (
                              <div className="px-2 py-1.5 text-xs text-muted-foreground">
                                No valid regions. Create one above.
                              </div>
                            ) : (
                              valid.map(r => (
                                <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <DialogFooter className="flex items-center justify-between gap-2 sm:justify-between">
          <div className="text-xs text-muted-foreground">
            {Object.keys(picks).length} of {conflicts.length} resolved
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="button-cancel-conflict">
              Cancel
            </Button>
            <Button
              disabled={!allResolved || applyMutation.isPending}
              onClick={() => applyMutation.mutate()}
              data-testid="button-apply-with-reassignments"
            >
              {applyMutation.isPending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
              Reassign &amp; apply
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkAssignPopover({
  selectedRegionIds,
  clientTypes,
  regions,
  onDone,
}: {
  selectedRegionIds: string[];
  clientTypes: ClientTypeRow[];
  regions: RegionRow[];
  onDone: () => void;
}) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"replace" | "add" | "remove">("add");
  const [picked, setPicked] = useState<string[]>([]);
  const [conflictDialog, setConflictDialog] = useState<{ open: boolean; conflicts: Conflict[]; payload: any }>({ open: false, conflicts: [], payload: null });

  const previewMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/regions/bulk-preview", {
        regionIds: selectedRegionIds,
        mode,
        clientTypeIds: picked,
      });
      return res.json() as Promise<PreviewResponse>;
    },
    onSuccess: (data) => {
      const payload = { regionIds: selectedRegionIds, mode, clientTypeIds: picked };
      if (data.conflicts.length === 0) {
        // No conflicts — apply immediately via the same atomic endpoint (no reassignments).
        applyMutation.mutate(payload);
      } else {
        setConflictDialog({ open: true, conflicts: data.conflicts, payload });
      }
    },
    onError: (e: any) => {
      toast({ title: "Error", description: e?.message || "Preview failed.", variant: "destructive" });
    },
  });

  const applyMutation = useMutation({
    mutationFn: async (payload: any) => {
      const res = await apiRequest("POST", "/api/regions/bulk-apply", { ...payload, reassignments: [] });
      return res.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/regions"] });
      toast({
        title: "Bulk update complete",
        description: `Updated ${data.regionsUpdated} region${data.regionsUpdated !== 1 ? "s" : ""}.`,
      });
      setOpen(false);
      setPicked([]);
      onDone();
    },
    onError: (e: any) => {
      toast({ title: "Error", description: e?.message || "Bulk update failed.", variant: "destructive" });
    },
  });

  const toggle = (id: string) => {
    setPicked(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  };

  const disabled = selectedRegionIds.length === 0;
  const isPending = previewMutation.isPending || applyMutation.isPending;

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            size="sm"
            disabled={disabled}
            data-testid="button-bulk-assign-client-types"
          >
            <Tag className="w-3.5 h-3.5 mr-1.5" />
            Assign client types ({selectedRegionIds.length})
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80" align="end">
          <div className="space-y-3">
            <div>
              <div className="text-sm font-medium">Bulk assign client types</div>
              <p className="text-xs text-muted-foreground">
                Apply to {selectedRegionIds.length} selected region{selectedRegionIds.length !== 1 ? "s" : ""}.
              </p>
            </div>

            <div className="flex gap-1 rounded-md border p-1">
              {(["add", "replace", "remove"] as const).map(m => (
                <button
                  key={m}
                  onClick={() => setMode(m)}
                  className={`flex-1 text-xs py-1 rounded ${mode === m ? "bg-primary text-primary-foreground" : "hover-elevate"}`}
                  data-testid={`button-bulk-mode-${m}`}
                >
                  {m === "add" ? "Add" : m === "replace" ? "Replace" : "Remove"}
                </button>
              ))}
            </div>

            <div className="space-y-1 max-h-72 overflow-y-auto">
              {clientTypes.map(ct => {
                const checked = picked.includes(ct.id);
                return (
                  <label
                    key={ct.id}
                    className="flex items-center gap-2 px-2 py-1.5 rounded hover-elevate cursor-pointer"
                    data-testid={`row-bulk-client-type-${ct.id}`}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => toggle(ct.id)}
                    />
                    <span className="text-sm flex-1">{ct.name}</span>
                  </label>
                );
              })}
            </div>

            <Button
              size="sm"
              className="w-full"
              disabled={picked.length === 0 || isPending}
              onClick={() => previewMutation.mutate()}
              data-testid="button-bulk-apply"
            >
              {isPending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
              Apply to {selectedRegionIds.length} region{selectedRegionIds.length !== 1 ? "s" : ""}
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      {conflictDialog.payload && (
        <ConflictResolutionDialog
          open={conflictDialog.open}
          onOpenChange={(o) => setConflictDialog(s => ({ ...s, open: o }))}
          conflicts={conflictDialog.conflicts}
          regions={regions}
          clientTypes={clientTypes}
          payload={conflictDialog.payload}
          onResolved={() => {
            setOpen(false);
            setPicked([]);
            onDone();
          }}
        />
      )}
    </>
  );
}

function DeleteRegionDialog({
  region,
  regions,
  onClose,
}: {
  region: RegionRow;
  regions: RegionRow[];
  onClose: () => void;
}) {
  const { toast } = useToast();
  // loading → idle → reassign | force-confirm
  const [phase, setPhase] = useState<"loading" | "idle" | "reassign" | "force-confirm">("loading");
  const [counts, setCounts] = useState({ leads: 0, clients: 0, invoices: 0 });
  const [targetRegionId, setTargetRegionId] = useState("");

  // Fetch impact as soon as the dialog opens
  useEffect(() => {
    setPhase("loading");
    fetch(`/api/regions/${region.id}/impact`, { credentials: "include" })
      .then(async r => {
        if (!r.ok) throw new Error(`Impact fetch failed: ${r.status}`);
        return r.json();
      })
      .then((data: { leadCount: number; clientCount: number; invoiceCount: number }) => {
        setCounts({ leads: data.leadCount ?? 0, clients: data.clientCount ?? 0, invoices: data.invoiceCount ?? 0 });
        setPhase("idle");
      })
      .catch(() => {
        // On error, default to zero counts so the user can still proceed with a simple empty-region delete.
        setCounts({ leads: 0, clients: 0, invoices: 0 });
        setPhase("idle");
        toast({ title: "Warning", description: "Could not load region contents. Proceeding with caution.", variant: "destructive" });
      });
  }, [region.id]);

  const deleteMutation = useMutation({
    mutationFn: async ({ force }: { force?: boolean } = {}) => {
      const url = force ? `/api/regions/${region.id}?force=true` : `/api/regions/${region.id}`;
      const res = await fetch(url, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as any).message || "Failed to delete region");
      }
      return null;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/regions"] });
      queryClient.invalidateQueries({ queryKey: ["/api/leads"] });
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
      queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
      toast({ title: "Region deleted", description: `"${region.name}" was deleted.` });
      onClose();
    },
    onError: (e: any) => {
      toast({ title: "Error", description: e.message || "Failed.", variant: "destructive" });
    },
  });

  const reassignMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/regions/${region.id}/delete-with-reassignment`, {
        reassignToRegionId: targetRegionId,
      });
      return res.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/regions"] });
      queryClient.invalidateQueries({ queryKey: ["/api/leads"] });
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
      queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
      toast({ title: "Region deleted", description: data.message });
      onClose();
    },
    onError: (e: any) => {
      toast({ title: "Error", description: e.message || "Failed.", variant: "destructive" });
    },
  });

  const otherRegions = regions.filter(r => r.id !== region.id);
  const hasRecords = counts.leads > 0 || counts.clients > 0 || counts.invoices > 0;

  const countParts: string[] = [];
  if (counts.leads) countParts.push(`${counts.leads} lead${counts.leads !== 1 ? "s" : ""}`);
  if (counts.clients) countParts.push(`${counts.clients} client${counts.clients !== 1 ? "s" : ""}`);
  if (counts.invoices) countParts.push(`${counts.invoices} invoice${counts.invoices !== 1 ? "s" : ""}`);
  const countSummary = countParts.join(", ");

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trash2 className="w-4 h-4 text-destructive" />
            Delete "{region.name}"
          </DialogTitle>
          <DialogDescription>
            {phase === "loading" && "Checking region contents…"}
            {phase === "idle" && !hasRecords && "This region is empty. Deleting it is safe and cannot be undone."}
            {phase === "idle" && hasRecords && `This region contains ${countSummary}. Choose how to proceed.`}
            {phase === "reassign" && `${countSummary} will be moved to the selected region before deletion.`}
            {phase === "force-confirm" && "This action cannot be undone."}
          </DialogDescription>
        </DialogHeader>

        {phase === "loading" && (
          <div className="flex items-center justify-center py-6" data-testid="loading-region-impact">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {phase === "idle" && hasRecords && (
          <div className="rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 px-3 py-2.5 text-sm text-amber-800 dark:text-amber-200" data-testid="callout-region-impact">
            This region contains{" "}
            {countParts.map((p, i) => (
              <span key={i}><strong>{p}</strong>{i < countParts.length - 1 ? (i === countParts.length - 2 ? " and " : ", ") : ""}</span>
            ))}.
          </div>
        )}

        {phase === "reassign" && (
          <div className="space-y-3 py-1">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Reassign records to</label>
              <Select value={targetRegionId} onValueChange={setTargetRegionId}>
                <SelectTrigger data-testid="select-reassign-to-region">
                  <SelectValue placeholder="Select a region…" />
                </SelectTrigger>
                <SelectContent>
                  {otherRegions.map(r => (
                    <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {phase === "force-confirm" && (
          <div className="rounded-md bg-destructive/10 border border-destructive/30 px-3 py-2.5 text-sm text-destructive" data-testid="callout-force-confirm">
            Are you sure? {countSummary} will lose their region assignment. This cannot be undone.
          </div>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose} data-testid="button-cancel-delete-region">
            Cancel
          </Button>

          {phase === "idle" && !hasRecords && (
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate()}
              data-testid="button-confirm-delete-region"
            >
              {deleteMutation.isPending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
              Delete region
            </Button>
          )}

          {phase === "idle" && hasRecords && (
            <>
              <Button
                variant="outline"
                onClick={() => setPhase("reassign")}
                data-testid="button-go-reassign"
              >
                Reassign them →
              </Button>
              <Button
                variant="destructive"
                onClick={() => setPhase("force-confirm")}
                data-testid="button-go-force-confirm"
              >
                Delete without reassigning
              </Button>
            </>
          )}

          {phase === "reassign" && (
            <>
              <Button variant="ghost" onClick={() => setPhase("idle")} data-testid="button-back-from-reassign">
                Back
              </Button>
              <Button
                variant="destructive"
                disabled={!targetRegionId || reassignMutation.isPending}
                onClick={() => reassignMutation.mutate()}
                data-testid="button-confirm-reassign-delete-region"
              >
                {reassignMutation.isPending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
                Reassign &amp; delete
              </Button>
            </>
          )}

          {phase === "force-confirm" && (
            <>
              <Button variant="ghost" onClick={() => setPhase("idle")} data-testid="button-back-from-force-confirm">
                Back
              </Button>
              <Button
                variant="destructive"
                disabled={deleteMutation.isPending}
                onClick={() => deleteMutation.mutate({ force: true })}
                data-testid="button-yes-delete-region"
              >
                {deleteMutation.isPending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
                Yes, delete
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InlineRenameRegion({
  region,
  onDone,
}: {
  region: RegionRow;
  onDone: () => void;
}) {
  const { toast } = useToast();
  const [value, setValue] = useState(region.name);

  const renameMutation = useMutation({
    mutationFn: async (name: string) => {
      const res = await apiRequest("PATCH", `/api/regions/${region.id}`, { name });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/regions"] });
      onDone();
    },
    onError: (e: any) => {
      toast({ title: "Error", description: e?.message || "Failed to rename region.", variant: "destructive" });
    },
  });

  const save = () => {
    const trimmed = value.trim();
    if (!trimmed || trimmed === region.name) { onDone(); return; }
    renameMutation.mutate(trimmed);
  };

  return (
    <div className="flex items-center gap-1 flex-1 min-w-0" data-testid={`inline-rename-${region.id}`}>
      <Input
        value={value}
        onChange={e => setValue(e.target.value)}
        onKeyDown={e => {
          if (e.key === "Enter") { e.preventDefault(); save(); }
          if (e.key === "Escape") { e.preventDefault(); onDone(); }
        }}
        autoFocus
        className="h-7 text-sm px-2 py-0 font-medium"
        disabled={renameMutation.isPending}
        data-testid={`input-rename-region-${region.id}`}
      />
      <Button
        size="icon"
        variant="ghost"
        className="h-6 w-6 shrink-0 text-green-600 hover:text-green-700"
        disabled={renameMutation.isPending}
        onClick={save}
        data-testid={`button-save-rename-region-${region.id}`}
      >
        {renameMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
      </Button>
      <Button
        size="icon"
        variant="ghost"
        className="h-6 w-6 shrink-0"
        disabled={renameMutation.isPending}
        onClick={onDone}
        data-testid={`button-cancel-rename-region-${region.id}`}
      >
        <X className="w-3.5 h-3.5" />
      </Button>
    </div>
  );
}

function Body() {
  const { data: regions = [], isLoading: regionsLoading } = useQuery<RegionRow[]>({ queryKey: ["/api/regions"] });
  const { data: users = [], isLoading: usersLoading } = useQuery<UserRow[]>({ queryKey: ["/api/admin/users"] });
  const { data: clientTypes = [], isLoading: ctLoading } = useQuery<ClientTypeRow[]>({ queryKey: ["/api/client-types"] });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [deletingRegion, setDeletingRegion] = useState<RegionRow | null>(null);
  const [renamingRegionId, setRenamingRegionId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const q = normalizeSearchStr(search);
    if (!q) return regions;
    return regions.filter(r => normalizeSearchStr(r.name).includes(q));
  }, [regions, search]);

  const allFilteredSelected = filtered.length > 0 && filtered.every(r => selected.has(r.id));
  const someFilteredSelected = filtered.some(r => selected.has(r.id));

  const toggleAll = () => {
    setSelected(prev => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filtered.forEach(r => next.delete(r.id));
      } else {
        filtered.forEach(r => next.add(r.id));
      }
      return next;
    });
  };

  const toggleOne = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (regionsLoading || usersLoading || ctLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (regions.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <MapPin className="w-10 h-10 mx-auto mb-2 opacity-50" />
        <p className="text-sm">No regions configured yet.</p>
        <p className="text-xs mt-1">Create regions from the Leads page → Region filter → Manage Regions.</p>
      </div>
    );
  }

  const ctNameById = new Map(clientTypes.map(c => [c.id, c.name]));

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Assign each region to one or more client types so it only appears in pickers and filters when those client types are selected. Leave empty for "all client types". You can also control which sales reps can see each region.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          placeholder="Search regions…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="flex-1 min-w-[180px] px-3 py-1.5 text-sm rounded-md border bg-background"
          data-testid="input-region-search"
        />
        <Button
          size="sm"
          variant="outline"
          onClick={toggleAll}
          disabled={filtered.length === 0}
          data-testid="button-toggle-all-regions"
        >
          {allFilteredSelected ? "Deselect all" : "Select all"}
          {search && <span className="ml-1 text-xs opacity-70">({filtered.length})</span>}
        </Button>
        <BulkAssignPopover
          selectedRegionIds={Array.from(selected)}
          clientTypes={clientTypes}
          regions={regions}
          onDone={() => setSelected(new Set())}
        />
      </div>

      {selected.size > 0 && (
        <div className="text-xs text-muted-foreground" data-testid="text-selected-count">
          {selected.size} region{selected.size !== 1 ? "s" : ""} selected
          <button
            type="button"
            className="ml-2 underline hover:text-foreground"
            onClick={() => setSelected(new Set())}
            data-testid="button-clear-selection"
          >
            clear
          </button>
        </div>
      )}

      <div className="rounded-md border divide-y">
        <div className="flex items-center gap-3 px-4 py-2 text-xs font-medium text-muted-foreground bg-muted/30">
          <Checkbox
            checked={allFilteredSelected ? true : someFilteredSelected ? "indeterminate" as any : false}
            onCheckedChange={toggleAll}
            data-testid="checkbox-header-toggle-all"
          />
          <div className="flex-1">Region</div>
          <div className="w-44 text-right">Client types</div>
          <div className="w-24 text-right">Users</div>
          <div className="w-8" />
        </div>
        {filtered.map(r => {
          const isSelected = selected.has(r.id);
          const isRenaming = renamingRegionId === r.id;
          const tagBadges = (r.clientTypeIds || []).map(id => ctNameById.get(id)).filter(Boolean);
          return (
            <div
              key={r.id}
              className={`group flex items-center gap-3 px-4 py-2.5 ${isSelected ? "bg-accent/40" : ""}`}
              data-testid={`row-region-${r.id}`}
            >
              <Checkbox
                checked={isSelected}
                onCheckedChange={() => toggleOne(r.id)}
                data-testid={`checkbox-region-${r.id}`}
              />
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <MapPin className="w-4 h-4 text-muted-foreground shrink-0" />
                {isRenaming ? (
                  <InlineRenameRegion region={r} onDone={() => setRenamingRegionId(null)} />
                ) : (
                  <>
                    <span className="font-medium truncate">{r.name}</span>
                    {tagBadges.length > 0 && (
                      <div className="flex gap-1 flex-wrap">
                        {tagBadges.slice(0, 3).map((n, i) => (
                          <Badge key={i} variant="secondary" className="text-[10px] px-1.5 py-0">{n}</Badge>
                        ))}
                        {tagBadges.length > 3 && (
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">+{tagBadges.length - 3}</Badge>
                        )}
                      </div>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground shrink-0"
                      onClick={() => setRenamingRegionId(r.id)}
                      data-testid={`button-rename-region-${r.id}`}
                    >
                      <Pencil className="w-3 h-3" />
                    </Button>
                  </>
                )}
              </div>
              {!isRenaming && (
                <>
                  <div className="w-44 flex justify-end">
                    <RegionClientTypePopover region={r} clientTypes={clientTypes} />
                  </div>
                  <div className="w-24 flex justify-end">
                    <RegionAssigneesPopover region={r} users={users} />
                  </div>
                  <div className="w-8 flex justify-end">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      onClick={() => setDeletingRegion(r)}
                      data-testid={`button-delete-region-${r.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="px-4 py-6 text-sm text-center text-muted-foreground">
            No regions match "{search}".
          </div>
        )}
      </div>

      {deletingRegion && (
        <DeleteRegionDialog
          region={deletingRegion}
          regions={regions}
          onClose={() => setDeletingRegion(null)}
        />
      )}
    </div>
  );
}

export function RegionAccessSettingsDialog({ open, onOpenChange, inPanel = false }: Props) {
  if (inPanel) {
    return (
      <div className="w-full h-full flex flex-col overflow-auto p-4 sm:p-6">
        <Body />
      </div>
    );
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            Region Settings
          </DialogTitle>
          <DialogDescription>
            Assign client types to regions and manage which users can see each region.
          </DialogDescription>
        </DialogHeader>
        <Body />
      </DialogContent>
    </Dialog>
  );
}
